import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { useSaveAsTemplate, type TemplateType } from '../../hooks/useTemplates';
import { EntityModal } from '../common/EntityModal';

interface SaveAsTemplateButtonProps {
  /** ID of the timeline / checklist / kibuddim to clone into a template */
  id: string;
  /** Type of entity */
  type: TemplateType;
  /** Suggested template name (defaults to "<title> Template") */
  defaultName?: string;
}

/**
 * Renders a "Save as Template" button + modal that copies the current event-scoped
 * timeline/checklist/kibuddim into a reusable template via the
 * `POST /event-ops/:id/save-as-template` endpoint.
 */
export function SaveAsTemplateButton({ id, type, defaultName }: SaveAsTemplateButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 text-gray-700 rounded-md hover:bg-gray-50"
        data-testid={`save-as-template-${type}-${id}`}
      >
        <Save size={14} />
        Save as Template
      </button>
      <SaveAsTemplateModal
        open={open}
        onClose={() => setOpen(false)}
        id={id}
        type={type}
        defaultName={defaultName}
      />
    </>
  );
}

/** The "Save as Template" dialog alone, for a caller with its own trigger (a menu item). */
export function SaveAsTemplateModal({
  open,
  onClose,
  id,
  type,
  defaultName,
}: SaveAsTemplateButtonProps & { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const saveAsTemplate = useSaveAsTemplate();

  useEffect(() => {
    if (open) setName(defaultName || '');
  }, [open, defaultName]);

  function handleClose() {
    onClose();
    setName('');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    saveAsTemplate.mutate(
      { id, templateName: name.trim(), type },
      { onSuccess: () => handleClose() },
    );
  }

  return (
    <EntityModal
      open={open}
      onClose={handleClose}
      title="Save as Template"
      size="sm"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="save-as-template-form"
            disabled={saveAsTemplate.isPending || !name.trim()}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="save-as-template-submit"
          >
            {saveAsTemplate.isPending ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      }
    >
      <form id="save-as-template-form" onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Saves a copy as a reusable template. Event-specific data (assignees, due dates, statuses)
          is stripped from the copy.
        </p>
        <div>
          <label
            htmlFor="template-name-input"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Template Name
          </label>
          <input
            id="template-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </form>
    </EntityModal>
  );
}
